import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import moment from 'moment';
import { observer } from 'mobx-react';
import { pinnedPanelStore } from './pinnedPanelStore';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import LinkifyText from 'ats/components/linkify-text/LinkifyText';
import { eventManager } from 'ats/utils/EventManager';
import { PIN_SCOPES, isTaskPinned } from 'ats/utils/taskPin';
import { getTaskMenuItems } from 'ats/utils/taskActions';
import { requestTaskFocus } from 'ats/utils/timelineTaskFocus';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { getApplicantTaskTheme } from 'ats/utils/applicantTaskTheme';
import { getJobApplicationTaskTheme } from 'ats/utils/jobApplicationTaskTheme';

const PINNED_QUERY = {
	pinned: true,
	sort: { column: 'createdAt', asc: false },
	pagination: { page: 1, limit: 100 }
};

async function fetchPinnedTasks(scope, entityId) {
	if (scope === PIN_SCOPES.JOB) {
		return jobApplicationStore.getTasks({
			query: { ...PINNED_QUERY, jobId: entityId }
		});
	}
	return applicantStore.getTasks({
		query: { ...PINNED_QUERY, applicants: [entityId] }
	});
}

function groupByApplicant(tasks) {
	const groups = [];
	tasks.forEach(task => {
		const applicant = task.jobApplication?.applicant;
		const key = applicant?.id ?? 'unknown';
		let group = groups.find(item => item.key === key);
		if (!group) {
			group = { key, name: applicant?.name || 'Candidate', tasks: [] };
			groups.push(group);
		}
		group.tasks.push(task);
	});
	return groups;
}

const TASK_STATUS_LABELS = {
	pending: 'Pending',
	completed: 'Completed',
	cancelled: 'Cancelled'
};

const normalizeTask = task => ({
	...task,
	assignments: task.assignments?.map(
		assignment => assignment.user ?? assignment
	)
});

function PinnedTaskCard({ scope, entityId, task, onChanged }) {
	const isJob = scope === PIN_SCOPES.JOB;
	const theme = isJob
		? getJobApplicationTaskTheme(task.type)
		: getApplicantTaskTheme(task.type);
	const typeLabel = (
		isJob ? jobApplicationStore.taskOptions : applicantStore.taskOptions
	).find(option => option.value === task.type)?.label;
	const statusLabel = TASK_STATUS_LABELS[task.status] || task.status;

	const goToTask = () => requestTaskFocus({ scope, entityId, taskId: task.id });

	return (
		<article
			className="pinned-task-card"
			role="button"
			tabIndex={0}
			title="Go to task"
			onClick={goToTask}
			onKeyDown={event => {
				if (event.target !== event.currentTarget) return;
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					goToTask();
				}
			}}>
			<header>
				<UserProfileAvatar
					imageUrl={task.createdByUser?.imageUrl}
					name={task.createdByUser?.name}
					tooltipPosition="right"
				/>
				<time dateTime={task.createdAt}>
					{moment(task.createdAt).format('lll')}
				</time>
				<div
					className="pinned-task-card-menu"
					onClick={event => event.stopPropagation()}
					onKeyDown={event => event.stopPropagation()}>
					<HoverMenu
						id={`pinned-${scope}-${task.id}`}
						position="left"
						buttonIcon={
							<img
								src="/ats/icons/three-dots-icon.svg"
								alt="Pinned task menu"
							/>
						}
						items={getTaskMenuItems({ scope, task, onSuccess: onChanged })}
					/>
				</div>
			</header>
			<span
				className="pinned-task-type"
				style={{ color: theme.accent, borderColor: theme.accent }}>
				<img src={theme.icon} alt="" />
				{typeLabel || task.type}
				<AtsTooltip
					text={statusLabel}
					placement="top"
					className={`pinned-task-status-dot is-${task.status}`}>
					<span className="is-sr-only">{statusLabel}</span>
				</AtsTooltip>
			</span>
			{task.description ? (
				<p>
					<LinkifyText text={task.description} />
				</p>
			) : null}
		</article>
	);
}

const STICKY_GAP = 12;

// The tab bar is sticky too and its offset depends on the profile header height.
function getScrollParent(element) {
	let node = element?.parentElement;
	while (node) {
		if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
		node = node.parentElement;
	}
	return null;
}

function useStickyLayoutBelowTabs(panelRef, isOpen) {
	const [layout, setLayout] = useState(null);

	useLayoutEffect(() => {
		const panel = panelRef.current;
		const tabs = panel
			?.closest('.tabbed-panel')
			?.querySelector(':scope > .tabs');
		if (!tabs) return undefined;
		const scrollParent = getScrollParent(panel);

		const measure = () => {
			const tabsTop = parseFloat(getComputedStyle(tabs).top) || 0;
			const top = tabsTop + tabs.offsetHeight + STICKY_GAP;
			const available = scrollParent?.clientHeight ?? window.innerHeight;
			setLayout({
				top,
				maxHeight: Math.max(available - top - STICKY_GAP, 160)
			});
		};
		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(tabs);
		if (scrollParent) observer.observe(scrollParent);
		return () => observer.disconnect();
	}, [panelRef, isOpen]);

	return layout;
}

function PinnedTasksPanel({ scope, entityId }) {
	const [tasks, setTasks] = useState([]);
	const isOpen = pinnedPanelStore.isOpen(scope);
	const panelRef = useRef(null);
	const stickyLayout = useStickyLayoutBelowTabs(panelRef, isOpen);

	const load = useCallback(async () => {
		if (!entityId) return;
		const result = await fetchPinnedTasks(scope, entityId);
		const rows =
			result?.status === 'success'
				? (result.data.rows || []).filter(isTaskPinned).map(normalizeTask)
				: [];
		setTasks(rows);
		pinnedPanelStore.setCount(scope, rows.length);
	}, [scope, entityId]);

	useEffect(() => {
		load();
		eventManager.on('taskPinChanged', load);
		eventManager.on('taskChanged', load);
		return () => {
			eventManager.off('taskPinChanged', load);
			eventManager.off('taskChanged', load);
		};
	}, [load]);

	useEffect(() => () => pinnedPanelStore.setCount(scope, 0), [scope]);

	if (!isOpen) return null;

	const renderCards = list =>
		list.map(task => (
			<PinnedTaskCard
				key={task.id}
				scope={scope}
				entityId={entityId}
				task={task}
				onChanged={load}
			/>
		));

	return (
		<aside
			ref={panelRef}
			className="pinned-tasks-panel"
			aria-label="Pinned"
			style={stickyLayout ?? undefined}>
			<div className="pinned-tasks-panel-header">
				<h4>Pinned</h4>
				<button
					type="button"
					className="pinned-tasks-panel-close"
					aria-label="Hide pinned"
					onClick={() => pinnedPanelStore.close(scope)}>
					<img src="/ats/icons/close-blue-icon.svg" alt="" />
				</button>
			</div>
			<div className="pinned-tasks-panel-body">
				{tasks.length === 0 ? (
					<p className="pinned-tasks-panel-empty">
						Pin a task or note from its menu to keep it here.
					</p>
				) : scope === PIN_SCOPES.JOB ? (
					groupByApplicant(tasks).map(group => (
						<section key={group.key} className="pinned-tasks-group">
							<h5>{group.name}</h5>
							{renderCards(group.tasks)}
						</section>
					))
				) : (
					renderCards(tasks)
				)}
			</div>
		</aside>
	);
}

export default observer(PinnedTasksPanel);
